/* eslint-disable jira/filename-case */

import type { FieldTypePayload } from './types.tsx';

/* Action status */

export const CANCELLED_STATUS = 'cancelled';
export const FINISHED_STATUS = 'finished';

/* Field IDs */
export const PARENT_FIELD_ID = 'parent' as const;
export const ISSUE_TYPE_FIELD_ID = 'issuetype' as const;
export const SUMMARY_FIELD_ID = 'summary' as const;
export const DESCRIPTION_FIELD_ID = 'description' as const;
export const PRIORITY_FIELD_ID = 'priority' as const;
export const LABELS_FIELD_ID = 'labels' as const;
export const DUE_DATE_FIELD_ID = 'duedate' as const;
export const ASSIGNEE_FIELD_ID = 'assignee' as const;
export const REPORTER_FIELD_ID = 'reporter' as const;
export const STATUS_FIELD_ID = 'status' as const;
export const PROJECT_FIELD_ID = 'project' as const;

export const DEFAULT_ORDER_FIELD_IDS: readonly [
	'issuetype',
	'summary',
	'description',
	'status',
	'assignee',
	'priority',
	'duedate',
	'labels',
	'reporter',
] = [
	ISSUE_TYPE_FIELD_ID,
	SUMMARY_FIELD_ID,
	DESCRIPTION_FIELD_ID,
	STATUS_FIELD_ID,
	ASSIGNEE_FIELD_ID,
	PRIORITY_FIELD_ID,
	DUE_DATE_FIELD_ID,
	LABELS_FIELD_ID,
	REPORTER_FIELD_ID,
] as const;

export const DEFAULT_ORDER_FIELD_IDS_SET: ReadonlySet<string> = new Set(DEFAULT_ORDER_FIELD_IDS);

export const EXCLUDED_FIELD_IDS_SET: Set<string> = new Set<string>([
	PARENT_FIELD_ID,
	PROJECT_FIELD_ID,
]);

export const CUSTOM_TEXT_FIELD_ID = 'custom-text-field-container';

export const CREATE_WITH_ROVO_MENU_ITEM = 'jira-aifc--create-with-rovo-menu-item';

export const fieldTypeToPayload: Record<string, FieldTypePayload> = {
	'com.atlassian.jira.plugin.system.customfieldtypes:textarea': {
		payload: 'ADF_VALUE',
		inputType: 'string',
		inputDescription: 'Provide content as either a plain string or markdown format.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:people': {
		payload: {
			accountId: 'VALUE',
		},
		inputType: 'array',
		inputDescription: 'Provide a list of Atlassian account IDs (AAIDs).',
	},
	// Jira custom field: User Picker (single)
	'com.atlassian.jira.plugin.system.customfieldtypes:userpicker': {
		payload: {
			accountId: 'VALUE',
		},
		inputType: 'object',
		inputDescription:
			'Provide a single Atlassian account ID (AAID). Value will be mapped to { accountId }.',
	},
	// Jira custom field: User Picker (multiple)
	'com.atlassian.jira.plugin.system.customfieldtypes:multiuserpicker': {
		payload: {
			accountId: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide a list of Atlassian account IDs (AAIDs). Each will be mapped to { accountId }.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:select': {
		payload: {
			value: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			"Provide the name of the option you want to select, e.g. 'TeamA'. Simply provide it as a string.",
		allowedValuesExtractorKey: 'value',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:multiselect': {
		payload: {
			value: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			"Provide the name of the options you want to select, e.g. ['TeamA', 'TeamB']. Simply provide it as an array of strings.",
		allowedValuesExtractorKey: 'value',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes': {
		payload: {
			value: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			"Provide the name of the options you want to select, e.g. ['TeamA', 'TeamB']. Simply provide it as an array of strings.",
		allowedValuesExtractorKey: 'value',
	},
	'com.pyxis.greenhopper.jira:gh-sprint': {
		inputType: 'integer',
		inputDescription:
			'Provide the id of the Sprint, e.g. 28. Ensure pass the id of the Sprint, not the name. To find the id call the \'FieldSearchTool\' tool with this input: {"fieldsToSearch":"sprint","project":"<jira-project>"} make sure you only swap out the project keep the field to search param the same.',
	},
	environment: {
		payload: 'ADF_VALUE',
		inputType: 'string',
		inputDescription:
			'Provide environment details as either a plain string or markdown format. Include relevant system, browser, or platform information.',
	},
	description: {
		payload: 'ADF_VALUE',
		inputType: 'string',
		inputDescription:
			'Provide the issue description as either a plain string or markdown format. Can include headings, lists, code blocks, and tables.',
	},
	assignee: {
		payload: {
			id: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			'Provide a single Atlassian account ID (AAID) for the person to assign the issue to, to unassign pass in null',
	},
	priority: {
		payload: {
			name: 'VALUE',
		},
		inputType: 'string',
		inputDescription: 'Provide a valid Jira priority level name string',
		allowedValuesExtractorKey: 'name',
	},
	reporter: {
		payload: {
			id: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			"Provide the Atlassian account ID (AAID) for the issue reporter. The AAID is always the value immediately after the last '/' in the format: ari:cloud:identity::user/<AAID>",
	},
	labels: {
		payload: 'VALUE',
		inputType: 'array',
		inputDescription:
			"Provide a list of label strings. Labels should be lowercase, no spaces (use hyphens). Example: ['backend', 'bug-fix', 'high-priority']",
	},
	parent: {
		inputType: 'string',
		inputDescription:
			'Provide the parent issue id or key. The id of a work item (issue). Example of id is 10000, or example key is ABC-1. To remove the parent pass in an empty string.',
	},
	comment: {
		isUpdateOperation: true,
		payload: [
			{
				add: {
					body: 'ADF_VALUE',
				},
			},
		],
		inputType: 'string',
		inputDescription:
			'Provide comment content as either a plain string or markdown format. Can include formatting such as bold, lists, and code blocks.',
	},
	'com.atlassian.jira.modules.servicemanagement.major-incident-entity:major-incident-entity-field-cftype':
		{
			inputType: 'string',
			inputDescription:
				"Provide as a string. If user asks for major incident, the value should be 'MAJOR_INCIDENT'. Otherwise, it should be an empty string ''",
		},
	project: {
		payload: {
			id: 'VALUE',
		},
		inputType: 'string',
		inputDescription: 'Provide the exact ID of the project that you want the issue within',
		allowedValuesExtractorKey: 'id',
	},
	issuetype: {
		payload: {
			name: 'VALUE',
		},
		inputType: 'string',
		inputDescription: 'Provide the name of the issue type you want to switch to.',
		allowedValuesExtractorKey: 'name',
	},
	'com.pyxis.greenhopper.jira:jsw-issue-color': {
		inputType: 'string',
		inputDescription:
			'Provide the name of the color. The only allowed values are: [purple, blue, green, teal, yellow, orange, grey, dark_purple, dark_blue, dark_green, dark_teal, dark_yellow, dark_orange, dark_grey]. If a color outside this list is requested, automatically select and use the closest allowed color instead—do not ask for clarification or input. Always ensure the value you provide is valid.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:float': {
		inputType: 'float',
		inputDescription:
			'Provide the value as a floating point number. Do not provide it as a string.',
	},
	issuelinks: {
		isUpdateOperation: true,
		payload: [
			{
				add: 'VALUE',
			},
		],
		inputType: 'object',
		inputDescription:
			'Provide input as object, example: { "type": {"name": "Blocks"}, "outwardIssue": {"key": "ABC-1"} }',
	},
	fixVersions: {
		payload: {
			name: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the name of the version(s) that you want to select, provide as an array of strings.',
		allowedValuesExtractorKey: 'name',
	},
	versions: {
		payload: {
			name: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the name of the version(s) that you want to select, provide as an array of strings.',
		allowedValuesExtractorKey: 'name',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:version': {
		payload: {
			name: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			'Provide the name of the version that you want to select, provide as a single string.',
		allowedValuesExtractorKey: 'name',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:multiversion': {
		payload: {
			name: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the name of the version(s) that you want to select, provide as an array of strings.',
		allowedValuesExtractorKey: 'name',
	},
	components: {
		payload: {
			id: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the ids of the component(s) that you want to select, provide as an array of strings. If names are provided instead of ids, they will be hydrated and mapped to { id }.',
	},
	'com.pyxis.greenhopper.jira:jsw-story-points': {
		inputType: 'integer',
		inputDescription:
			'Please provide the story points as an integer. e.g. 7. Do not provide it as a string.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:radiobuttons': {
		payload: {
			value: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			'Provide the value of the option you want to select, e.g. OldProject. Simply provide a single string.',
		allowedValuesExtractorKey: 'value',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:multigrouppicker': {
		payload: {
			name: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the exact name of the group(s) that you want to select, provide as an array of strings.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:project': {
		payload: {
			key: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			'Provide the exact key of the project that you want to select, provide as a single string.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:jwm-category': {
		payload: {
			value: 'VALUE',
		},
		inputType: 'string',
		inputDescription:
			'Provide the exact name of the category that you want to select, provide as a single string.',
		allowedValuesExtractorKey: 'value',
	},
	'com.atlassian.servicedesk:sd-customer-organizations': {
		payload: 'VALUE',
		inputType: 'array',
		inputDescription:
			'Provide the exact id(s) of the organisations that you want to select, provide as an array of integers.',
	},
	'com.atlassian.servicedesk:sd-request-participants': {
		payload: {
			accountId: 'VALUE',
		},
		inputType: 'array',
		inputDescription:
			'Provide the exact AAID(s) of the users that you want to select, provide as an array of strings.',
	},
	'com.atlassian.servicedesk:vp-origin': {
		inputType: 'string',
		inputDescription:
			'Provide the exact id of the request type that you want to select, provide as a single string.',
	},
	timetracking: {
		inputType: 'object',
		inputDescription:
			'Provide an object, example object for all timetracking fields: { "originalEstimate": "3d", "remainingEstimate": "5w" }. Only provide fields you want to update in this object. Ensure you give times in this format: 2w 4d 6h 45m.',
	},
	'com.atlassian.jira.plugin.system.customfieldtypes:url': {
		inputType: 'string',
		inputDescription: 'Provide the URL that you want to select, provide as a single string.',
	},
};
